import React, { useEffect, useMemo, useRef, useState } from 'react';

// A keyboard-first command palette (⌘K). The parent passes structured
// context (sites, categories, current mode) and handlers; the palette
// assembles the command list, filters on the query, and drives selection
// with the arrow keys / Enter. Everything it does is an existing in-app
// action — jump to a saved site, switch category or mode, open an overlay,
// or run a one-off action.
export default function CommandPalette({
  open, onClose,
  sites = [], categories = [],
  mode, hasQR, hasSC, isAdmin,
  hasLockedResult, canSave,
  onJumpSite, onCategory, onMode, onOverlay,
  onSave, onBuyCredits, onDemo, onUnlock,
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // ---- build the full command set ----
  const commands = useMemo(() => {
    const cmds = [];

    sites.forEach((s) => {
      const score = s.latest_score ?? s.last_score;
      cmds.push({
        section: 'Pipeline',
        icon: '📍',
        label: s.name,
        hint: score != null ? `score ${score}` : 'jump',
        keywords: `${s.name} ${s.category || ''}`,
        run: () => onJumpSite(s),
      });
    });

    if (mode === 'quick') {
      categories.forEach((c) => {
        cmds.push({
          section: 'Category',
          icon: '◆',
          label: c.label,
          hint: 'set category',
          keywords: `category ${c.label} ${c.key}`,
          run: () => onCategory(c.key),
        });
      });
    }

    [
      ['pipeline', 'Pipeline'],
      ['reports', 'Reports'],
      ['predictive', 'Predictive Analytics'],
      ['invoices', 'Invoices'],
      ['account', 'Account'],
      ...(isAdmin ? [['admin', 'Admin · Brand verification']] : []),
    ].forEach(([key, label]) => {
      cmds.push({
        section: 'Navigate',
        icon: '▤',
        label: `Open ${label}`,
        hint: 'view',
        keywords: `open ${label} ${key}`,
        run: () => onOverlay(key),
      });
    });

    if (hasQR && hasSC) {
      const to = mode === 'quick' ? 'feasibility' : 'quick';
      cmds.push({
        section: 'Action',
        icon: '⇄',
        label: mode === 'quick' ? 'Switch to centre feasibility' : 'Switch to site report',
        hint: 'mode',
        keywords: 'switch mode feasibility site report',
        run: () => onMode(to),
      });
    }
    if (hasLockedResult && onUnlock) {
      cmds.push({
        section: 'Action',
        icon: '🔓',
        label: 'Unlock full report',
        hint: 'credit',
        keywords: 'unlock full report economics',
        run: onUnlock,
      });
    }
    if (canSave) {
      cmds.push({
        section: 'Action',
        icon: '＋',
        label: 'Save current site to pipeline',
        hint: 'save',
        keywords: 'save site pipeline pin',
        run: onSave,
      });
    }
    cmds.push({
      section: 'Action',
      icon: '◷',
      label: 'Buy report credits',
      hint: 'billing',
      keywords: 'buy credits purchase billing',
      run: onBuyCredits,
    });
    if (onDemo) {
      cmds.push({
        section: 'Action',
        icon: '▶',
        label: 'Run demo report',
        hint: 'demo',
        keywords: 'demo sample example',
        run: onDemo,
      });
    }
    return cmds;
  }, [sites, categories, mode, hasQR, hasSC, isAdmin, hasLockedResult, canSave,
      onJumpSite, onCategory, onMode, onOverlay, onSave, onBuyCredits, onDemo, onUnlock]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      (c.label + ' ' + (c.keywords || '')).toLowerCase().includes(q)
    );
  }, [commands, query]);

  useEffect(() => { setActive(0); }, [query]);

  // keep the active row in view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const run = (i) => { filtered[i]?.run(); onClose(); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(active); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  // render grouped, but keep a flat index for keyboard nav
  let idx = -1;
  let lastSection = null;

  return (
    <div style={styles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sq-glass" style={styles.pal}>
        <div style={styles.searchRow}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="7" stroke="var(--s-faint)" strokeWidth="2" />
            <path d="M20 20l-3.5-3.5" stroke="var(--s-faint)" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search sites, categories, actions…"
            style={styles.input}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd style={styles.esc}>esc</kbd>
        </div>
        <div ref={listRef} style={styles.list}>
          {filtered.length === 0 && (
            <div style={styles.empty}>No matches for “{query}”.</div>
          )}
          {filtered.map((c) => {
            idx += 1;
            const i = idx;
            const showHeader = c.section !== lastSection;
            lastSection = c.section;
            return (
              <React.Fragment key={i}>
                {showHeader && <div style={styles.secHead}>{c.section}</div>}
                <div
                  data-idx={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(i)}
                  style={{ ...styles.item, ...(i === active ? styles.itemActive : {}) }}
                >
                  <span style={styles.icon}>{c.icon}</span>
                  <span style={styles.label}>{c.label}</span>
                  <span style={styles.hint}>{c.hint}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 60,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    paddingTop: '13vh',
    background: 'rgba(5,6,8,0.55)',
    backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
  },
  pal: {
    width: 'min(560px, 92vw)', overflow: 'hidden',
    animation: 'fadeUp 0.16s ease',
  },
  searchRow: {
    display: 'flex', alignItems: 'center', gap: 11,
    padding: '15px 18px', borderBottom: '1px solid var(--s-line)',
  },
  input: {
    flex: 1, background: 'transparent', border: 'none', outline: 'none',
    color: 'var(--s-text)', font: '500 15.5px var(--s-sans)',
  },
  esc: {
    font: '600 10px var(--s-mono)', color: 'var(--s-faint)',
    border: '1px solid var(--s-line)', borderRadius: 5, padding: '2px 6px',
    background: 'rgba(0,0,0,0.3)',
  },
  list: { maxHeight: 360, overflowY: 'auto', padding: 8 },
  empty: { padding: '20px 12px', color: 'var(--s-faint)', fontSize: 13 },
  secHead: {
    font: '600 9.5px var(--s-sans)', letterSpacing: '0.14em',
    textTransform: 'uppercase', color: 'var(--s-faint)',
    padding: '10px 12px 5px',
  },
  item: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
    fontSize: 13.5, color: 'var(--s-text)',
  },
  itemActive: { background: 'rgba(255,106,61,0.12)' },
  icon: {
    width: 24, height: 24, flexShrink: 0,
    display: 'grid', placeItems: 'center',
    borderRadius: 7, background: 'var(--s-w06)', fontSize: 12,
  },
  label: { flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hint: { font: '500 10.5px var(--s-mono)', color: 'var(--s-faint)', flexShrink: 0 },
};
